import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import SiteLayout from '@/components/feature/SiteLayout';
import MovieCard from '@/components/feature/MovieCard';
import { useDocumentMeta } from '@/hooks/useDocumentMeta';
import { searchMovies, getFeaturedMovies } from '@/utils/movie';

const hotKeywords = ['善良的小姨子', '剧情', '家庭', '悬疑', '爱情', '科幻'];

export default function Search() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [keyword, setKeyword] = useState(searchParams.get('q') ?? '');

  useEffect(() => {
    setKeyword(searchParams.get('q') ?? '');
  }, [searchParams]);

  const results = useMemo(() => searchMovies(keyword), [keyword]);
  const recommendations = useMemo(() => getFeaturedMovies().slice(0, 4), []);

  useDocumentMeta({
    title: keyword ? `“${keyword}”搜索结果 - 善良的小姨子影视站` : '搜索电影 - 善良的小姨子影视站',
    description: keyword
      ? `在善良的小姨子影视站搜索“${keyword}”，共找到 ${results.length} 部相关影片，提供剧情简介与在线观看入口。`
      : '善良的小姨子影视站影片搜索，输入片名、类型、导演或主演即可查找想看的内容。',
    keywords: '电影搜索,善良的小姨子,在线观看,影视搜索',
  });

  const submit = (event: FormEvent) => {
    event.preventDefault();
    const trimmed = keyword.trim();
    setSearchParams(trimmed ? { q: trimmed } : {});
  };

  return (
    <SiteLayout>
      <section className="w-full px-4 md:px-8 pt-10 md:pt-14 pb-8 border-b border-background-800 bg-background-950">
        <span className="text-xs font-display tracking-[0.3em] text-primary-400">SEARCH</span>
        <h1 className="mt-2 text-2xl md:text-4xl font-heading font-black text-foreground-50">搜索影片</h1>
        <p className="mt-3 text-sm text-foreground-400">输入片名、类型、导演或主演，即时查看匹配结果。</p>

        <form onSubmit={submit} className="mt-6 max-w-2xl">
          <div className="flex items-center gap-2 p-2 rounded-lg bg-background-900 border border-background-700 focus-within:border-primary-500 transition-colors">
            <span className="w-10 h-10 flex items-center justify-center text-foreground-400 shrink-0">
              <i className="ri-search-line text-lg"></i>
            </span>
            <input
              type="text"
              value={keyword}
              onChange={(event) => setKeyword(event.target.value)}
              placeholder="例如：善良的小姨子、悬疑、林知远…"
              className="flex-1 bg-transparent outline-none text-sm md:text-base text-foreground-100 placeholder:text-foreground-500 py-2"
            />
            {keyword && (
              <button
                type="button"
                onClick={() => {
                  setKeyword('');
                  setSearchParams({});
                }}
                className="w-9 h-9 rounded-md flex items-center justify-center text-foreground-400 hover:text-foreground-100 hover:bg-background-800 transition-colors cursor-pointer"
                aria-label="清空"
              >
                <i className="ri-close-line"></i>
              </button>
            )}
            <button
              type="submit"
              className="px-4 md:px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap shrink-0"
            >
              搜索
            </button>
          </div>
        </form>

        <div className="mt-4 flex items-center gap-2 flex-wrap">
          <span className="text-xs text-foreground-500">热门搜索：</span>
          {hotKeywords.map((item) => (
            <button
              key={item}
              type="button"
              onClick={() => {
                setKeyword(item);
                setSearchParams({ q: item });
              }}
              className="px-3 py-1 rounded-full text-xs bg-background-800 text-foreground-300 hover:bg-background-700 hover:text-foreground-100 transition-colors cursor-pointer whitespace-nowrap"
            >
              {item}
            </button>
          ))}
        </div>
      </section>

      <section className="w-full px-4 md:px-8 py-8 md:py-10 bg-background-950 min-h-[40vh]">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3 flex-wrap">
            <span className="px-3 py-1 rounded-full text-xs bg-primary-500/15 text-primary-300 border border-primary-500/30 whitespace-nowrap">
              影片
            </span>
            <span className="text-sm text-foreground-400">
              {keyword.trim() ? (
                <>
                  关键词 <span className="text-foreground-100">“{keyword.trim()}”</span> 共找到{' '}
                  <span className="text-primary-400 font-semibold">{results.length}</span> 个结果
                </>
              ) : (
                '输入关键词开始搜索'
              )}
            </span>
          </div>
          {keyword.trim() && results.length > 0 && (
            <Link
              to="/movie-category"
              className="text-sm text-primary-400 hover:text-primary-300 transition-colors cursor-pointer whitespace-nowrap"
            >
              去分类页浏览
            </Link>
          )}
        </div>

        {results.length > 0 ? (
          <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
            {results.map((movie) => (
              <MovieCard key={movie.id} movie={movie} />
            ))}
          </div>
        ) : (
          <div className="mt-8">
            <div className="flex flex-col items-center justify-center text-center py-14 rounded-lg bg-background-900 border border-background-800">
              <span className="w-16 h-16 rounded-full bg-background-800 flex items-center justify-center text-foreground-400">
                <i className="ri-search-eye-line text-3xl"></i>
              </span>
              <h3 className="mt-5 text-lg font-semibold text-foreground-100">
                {keyword.trim() ? '没有找到匹配的影片' : '还没有搜索内容'}
              </h3>
              <p className="mt-2 text-sm text-foreground-400 max-w-md">
                {keyword.trim()
                  ? '换个关键词试试，或返回首页看看本站精选影片。'
                  : '在上方输入片名、类型、导演或主演，即可找到想看的内容。'}
              </p>
              <div className="mt-6 flex items-center gap-3 flex-wrap justify-center">
                <Link
                  to="/"
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-home-4-line"></i>
                  返回首页
                </Link>
                <Link
                  to="/movie-category"
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-background-800 border border-background-700 text-foreground-100 text-sm font-medium hover:bg-background-700 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-grid-line"></i>
                  浏览全部影片
                </Link>
              </div>
            </div>

            <div className="mt-12">
              <div className="flex items-center gap-3 mb-6">
                <span className="w-1 h-6 rounded-full bg-accent-500"></span>
                <h2 className="text-lg md:text-xl font-heading font-black text-foreground-50">为你精选</h2>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
                {recommendations.map((movie) => (
                  <MovieCard key={movie.id} movie={movie} />
                ))}
              </div>
            </div>
          </div>
        )}
      </section>
    </SiteLayout>
  );
}